import { ArrowLeft, KeyboardArrowLeft, KeyboardArrowRight, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { useEffect, useState } from 'react'; 
import { Helpers } from '../../services/Helpers';

// Material-UI inspired styles
const muiStyles = {
  paper: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
    overflow: 'hidden'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
  },
  button: {
    backgroundColor: '#1976d2',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    padding: '12px',
    cursor: 'pointer',
    transition: 'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'
  },
  iconButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: 'rgba(0, 0, 0, 0.54)',
    border: 'none',
    borderRadius: '50%',
    padding: '12px',
    cursor: 'pointer',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
  },
  chip: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    color: 'rgba(0, 0, 0, 0.87)',
    borderRadius: '16px',
    padding: '6px 12px',
    fontSize: '0.8125rem',
    fontWeight: '500'
  },
  typography: {
    h4: {
      fontSize: '2.125rem',
      fontWeight: '400',
      lineHeight: '1.235',
      color: 'rgba(0, 0, 0, 0.87)',
      margin: '0 0 24px 0'
    },
    h6: {
      fontSize: '1.25rem',
      fontWeight: '500',
      lineHeight: '1.6',
      color: 'rgba(0, 0, 0, 0.87)',
      margin: '0'
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: '400',
      lineHeight: '1.43',
      color: 'rgba(0, 0, 0, 0.6)',
      margin: '8px 0 0 0'
    }
  }
};

const ImageGallery = ({ 
  images = [], 
  title = "Image Gallery",
  thumbnailPosition = "bottom",
  elevation = 3,
  spacing = 2,
  activeIndex=0,
  baseUrl
}) => {
  const [selectedIndex, setSelectedIndex] = useState(activeIndex);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleImageSelect = (index) => {
    setSelectedIndex(index);
    setIsZoomed(false);
  };

  const handlePrevious = () => {
    const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
    setSelectedIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
    setSelectedIndex(newIndex);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') handlePrevious();
      if (event.key === 'ArrowRight') handleNext();
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedIndex]);

  if (!images.length) {
    return (
      <Box sx={{
        ...muiStyles.paper,
        padding: '48px 24px',
        textAlign: 'center',
        bgColor: '#f5f5f5'
      }}>
        <p style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1.125rem' }}>
          No images to display
        </p>
      </Box>
    );
  }

  const currentImage = images[selectedIndex];

  const ThumbnailGrid = () => (
    <Box style={{
      display: 'flex',
      gap: `${spacing * 8}px`,
      padding: '16px',
      overflowX: thumbnailPosition === 'top' || thumbnailPosition === 'bottom' ? 'auto' : 'visible',
      overflowY: thumbnailPosition === 'left' || thumbnailPosition === 'right' ? 'auto' : 'visible',
      flexDirection: thumbnailPosition === 'left' || thumbnailPosition === 'right' ? 'column' : 'row',
      maxHeight: thumbnailPosition === 'left' || thumbnailPosition === 'right' ? '400px' : 'auto',
      backgroundColor: '#fafafa',
      borderRadius: '4px'
    }}>
      {images.map((image, index) => (
        <Box
          key={index}
          style={{
            ...muiStyles.card,
            width: '80px',
            height: '80px',
            cursor: 'pointer',
            flexShrink: 0,
            border: selectedIndex === index ? '3px solid #1976d2' : '3px solid transparent',
            transform: selectedIndex === index ? 'scale(1.05)' : 'scale(1)',
            boxShadow: selectedIndex === index 
              ? '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)'
              : muiStyles.card.boxShadow
          }}
          onClick={() => handleImageSelect(index)}
          onMouseEnter={(e) => {
            if (selectedIndex !== index) {
              e.target.style.transform = 'scale(1.02)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedIndex !== index) {
              e.target.style.transform = 'scale(1)';
            }
          }}
        >
          <img
            src={`${baseUrl}/md/${image.name}`}
            // src={typeof image === 'string' ? image : image}
            alt={ `Thumbnail ${index + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          {selectedIndex === index && (
            <Box style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: '#1976d2',
              borderRadius: '50%',
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box style={{
                width: '6px',
                height: '6px',
                backgroundColor: '#fff',
                borderRadius: '50%'
              }} />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  const MainImageCard = () => (
    <Box style={{
      ...muiStyles.paper,
      position: 'relative',
      backgroundColor: '#f5f5f5',
      minHeight: '400px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Box style={{
        position: 'relative',
        maxWidth: '100%',
        maxHeight: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <img
          src={`${baseUrl}/lg/${currentImage.name}`}
          alt={`Image ${selectedIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '400px',
            objectFit: 'contain',
            transform: isZoomed ? 'scale(1.5)' : 'scale(1)',
            transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: isZoomed ? 'zoom-out' : 'zoom-in'
          }}
          onClick={() => setIsZoomed(!isZoomed)}
        />
      </Box>

      {/* Navigation Buttons */}
      {images.length > 1 && (
        <>
          <button
            style={{
              ...muiStyles.button,
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            onClick={handlePrevious}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1565c0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#1976d2';
            }}
          >
            <KeyboardArrowLeft size={24} />
          </button>
          
          <button
            style={{
              ...muiStyles.button,
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
            onClick={handleNext}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#1565c0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#1976d2';
            }}
          >
            <KeyboardArrowRight size={24} />
          </button>
        </>
      )}

      {/* Zoom Button */}
      <button
        style={{
          ...muiStyles.iconButton,
          position: 'absolute',
          top: '16px',
          right: '16px'
        }}
        onClick={() => setIsZoomed(!isZoomed)}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.08)';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
        }}
      >
        {isZoomed ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
      </button>

      {/* Image Counter Chip */}
      <Box style={{
        ...muiStyles.chip,
        position: 'absolute',
        bottom: '16px',
        right: '16px'
      }}>
        {selectedIndex + 1} of {images.length}
      </Box>
    </Box>
  );

  const isVertical = thumbnailPosition === 'left' || thumbnailPosition === 'right';
  const containerStyle = {
    display: 'flex',
    flexDirection: isVertical ? 'row' : 'column',
    gap: `${spacing * 8}px`,
    padding: '16px',
    backgroundColor: '#fafafa',
    borderRadius: '8px'
  };

  const mainImageOrder = thumbnailPosition === 'top' || thumbnailPosition === 'left' ? 2 : 1;
  const thumbnailOrder = thumbnailPosition === 'top' || thumbnailPosition === 'left' ? 1 : 2;

  return (
    <Box style={{}}>      
      <Box style={containerStyle}>
        <Box style={{
          order: mainImageOrder,
          flex: isVertical ? '1' : 'none'
        }}>
          <MainImageCard />
        </Box>
        
        <Box style={{
          order: thumbnailOrder,
          width: isVertical ? '120px' : 'auto'
        }}>
          <ThumbnailGrid />
        </Box>
      </Box>
    </Box>
  );
};

// Demo component
export default function GalleryView({images,selectedIndex,baseUrl}) {
  const sampleImages = [
    {
      url: 'https://picsum.photos/800/600?random=1',
      alt: 'Beautiful landscape',
      title: 'Mountain Vista',
      description: 'A breathtaking view of snow-capped mountains during golden hour'
    },
    {
      url: 'https://picsum.photos/800/600?random=2',
      alt: 'Urban architecture',
      title: 'City Lights',
      description: 'Modern skyscrapers illuminated against the evening sky'
    },
    {
      url: 'https://picsum.photos/800/600?random=3',
      alt: 'Ocean waves',
      title: 'Coastal Serenity',
      description: 'Gentle waves washing onto a pristine sandy beach'
    },
    {
      url: 'https://picsum.photos/800/600?random=4',
      alt: 'Forest pathway',
      title: 'Forest Trail',
      description: 'A peaceful walking path through an ancient forest'
    },
    {
      url: 'https://picsum.photos/800/600?random=5',
      alt: 'Desert landscape',
      title: 'Desert Mirage',
      description: 'Vast sand dunes stretching endlessly under the desert sun'
    },
    {
      url: 'https://picsum.photos/800/600?random=6',
      alt: 'Winter wonderland',
      title: 'Winter Magic',
      description: 'Fresh snow blanketing a quiet mountain village'
    }
  ];

  const [position, setPosition] = useState('bottom');
  const [spacing, setSpacing] = useState(1);

  return (
    <Box sx={{
      bgcolor: '#f5f5f5',
    }}>

      {/* Gallery */}
      <ImageGallery 
        images={images}
        title="Material Design Gallery"
        thumbnailPosition={position}
        spacing={spacing}
        activeIndex={selectedIndex}
        baseUrl={baseUrl}
      />
    </Box>
  );
}